import { detectEcommercePlatform } from './connectionPlatform.ts';
import {
  connectionHasAmazonInventoryCsv,
  connectionHasShopifyInventoryCsv,
  isCsvLikeConnection,
} from './inventoryCsvConnectionChannels.ts';

/**
 * Derives the "Data Source" labels shown in the Current Stock tab's source picker.
 *
 * Extracted from OrganisationInventoryTab's `availableSources` memo so tests can assert on
 * the real derivation instead of a hand-copied replica. The replica had already drifted --
 * it omitted the CSV-import labels entirely, so it could not have caught a regression in
 * the CSV branches or in label ordering.
 *
 * Label order is meaningful: it is the order the options are rendered in, and
 * `getSourceDisplayText` compares selected-vs-available lengths to decide when to collapse
 * the picker to "All Sources".
 *
 * Veeqo intentionally has no CSV variant -- Veeqo inventory only arrives over the API, so a
 * connection that claims to be Veeqo-over-CSV is not a recognised source.
 */
export function getInventoryTabAvailableSources(
  connections: Record<string, unknown> | unknown[] | null | undefined,
): string[] {
  if (!connections) return [];

  const list = (Array.isArray(connections) ? connections : Object.values(connections)) as Record<
    string,
    unknown
  >[];

  const hasShopify = list.some((conn) => detectEcommercePlatform(conn) === 'shopify');

  const hasShopifyCsv = list.some((conn) => {
    if (connectionHasShopifyInventoryCsv(conn)) return true;
    if (!isCsvLikeConnection(conn)) return false;
    const rawSub = String(conn.ecommerce_subtype || '').toLowerCase();
    return rawSub === 'shopify' && !connectionHasAmazonInventoryCsv(conn);
  });

  const hasAmazon = list.some((conn) => detectEcommercePlatform(conn) === 'amazon');

  const hasAmazonCsv = list.some((conn) => {
    if (connectionHasAmazonInventoryCsv(conn)) return true;
    if (!isCsvLikeConnection(conn)) return false;
    const rawSub = String(conn.ecommerce_subtype || '').toLowerCase();
    return rawSub === 'amazon' && !connectionHasShopifyInventoryCsv(conn);
  });

  const hasVeeqo = list.some((conn) => detectEcommercePlatform(conn) === 'veeqo');

  const sources: string[] = [];
  if (hasShopify) sources.push('Shopify');
  if (hasShopifyCsv) sources.push('Shopify CSV Import');
  if (hasAmazon) sources.push('Amazon');
  if (hasAmazonCsv) sources.push('Amazon CSV Import');
  if (hasVeeqo) sources.push('Veeqo');

  return sources;
}

// ---------------------------------------------------------------------------
// Saved inventory source (FIG-442 · UC-251)
//
// organisations.inventory_source_config.primary_source is the one source whose
// stock counts as Stock Held. It is set in Settings › Reports and Charts › Data
// Sources (update-merchant-inventory-source-v1) and followed by the Inventory
// tab. Imported by the edge function too, hence the explicit .ts extensions.
// ---------------------------------------------------------------------------

/** Exactly the values normalizeInventorySource (skuReportGenerator.ts) accepts. */
export const INVENTORY_SOURCE_VALUES = ['all', 'amazon', 'amazon_csv', 'shopify', 'shopify_csv', 'veeqo'] as const;

export type InventorySourceValue = (typeof INVENTORY_SOURCE_VALUES)[number];

/** What "all" means, without the "(Default)" suffix: used in read-only lines. */
export const INVENTORY_SOURCE_DEFAULT_LABEL = "Each connection's own stock";

/**
 * Option labels. Except for `all`, they are the labels the Inventory data
 * functions expect in their `sources` list (getInventoryTabAvailableSources).
 */
export const INVENTORY_SOURCE_LABELS: Record<InventorySourceValue, string> = {
  all: `${INVENTORY_SOURCE_DEFAULT_LABEL} (Default)`,
  shopify: 'Shopify',
  shopify_csv: 'Shopify CSV Import',
  amazon: 'Amazon',
  amazon_csv: 'Amazon CSV Import',
  veeqo: 'Veeqo',
};

export interface InventorySourceOption {
  value: InventorySourceValue;
  label: string;
}

const isInventorySourceValue = (value: unknown): value is InventorySourceValue =>
  typeof value === 'string' && (INVENTORY_SOURCE_VALUES as readonly string[]).includes(value);

/** The sources a merchant can connect, in the order getInventoryTabAvailableSources lists them. */
const CONNECTABLE_INVENTORY_SOURCES = ['shopify', 'shopify_csv', 'amazon', 'amazon_csv', 'veeqo'] as const;

type ConnectableInventorySource = (typeof CONNECTABLE_INVENTORY_SOURCES)[number];

/** Stored values of the sources the merchant has connected, in display order. */
export function getConnectedInventorySourceValues(
  connections: Record<string, unknown> | unknown[] | null | undefined,
): ConnectableInventorySource[] {
  const labels = getInventoryTabAvailableSources(connections);
  return CONNECTABLE_INVENTORY_SOURCES.filter((value) => labels.includes(INVENTORY_SOURCE_LABELS[value]));
}

/** The dropdown: "all" first, then each connected source. */
export function getInventorySourceOptions(
  connections: Record<string, unknown> | unknown[] | null | undefined,
): InventorySourceOption[] {
  return [
    { value: 'all', label: INVENTORY_SOURCE_LABELS.all },
    ...getConnectedInventorySourceValues(connections).map((value) => ({ value, label: INVENTORY_SOURCE_LABELS[value] })),
  ];
}

/** True for `all` and for any source the merchant has connected. */
export function isInventorySourceConnected(
  value: InventorySourceValue,
  connections: Record<string, unknown> | unknown[] | null | undefined,
): boolean {
  return value === 'all' || getConnectedInventorySourceValues(connections).some((connected) => connected === value);
}

/**
 * The saved value as stored, or `null` when nothing is saved. A value outside
 * the allowed six is returned as the raw string so the resolver can report it
 * as not connected.
 */
export function readSavedInventorySource(config: unknown): string | null {
  if (!config || typeof config !== 'object') return null;
  const primary = (config as { primary_source?: unknown }).primary_source;
  return typeof primary === 'string' && primary.length > 0 ? primary : null;
}

export type InventorySourceOrigin = 'default' | 'saved' | 'fallback';

export interface ResolvedInventorySource {
  /** The source in effect: the saved one when connected, otherwise `all`. */
  value: InventorySourceValue;
  /** What was saved (`all` when nothing is). Differs from `value` only for a fallback. */
  saved: string;
  /** 'default': nothing saved or `all`; 'saved': a connected source; 'fallback': the saved source is gone. */
  origin: InventorySourceOrigin;
  /** Short name for read-only lines, e.g. "Amazon" or "Each connection's own stock". */
  label: string;
  /** Option label as the Settings dropdown shows it. */
  optionLabel: string;
  /** Labels for inventory-data-v6 / fetch-organisation-skus: every connected source, or just the saved one. */
  sources: string[];
}

/**
 * What the Inventory tab, Reports and the Settings row show for the saved
 * setting. Never yields an empty `sources` list unless nothing is connected:
 * a saved source that is no longer connected falls back to every connection
 * (and says so through `origin: 'fallback'`) instead of showing nothing.
 */
export function resolveInventorySource(
  config: unknown,
  connections: Record<string, unknown> | unknown[] | null | undefined,
): ResolvedInventorySource {
  const allSources = getInventoryTabAvailableSources(connections);
  const savedRaw = readSavedInventorySource(config);

  const everything = (origin: InventorySourceOrigin): ResolvedInventorySource => ({
    value: 'all',
    saved: savedRaw ?? 'all',
    origin,
    label: INVENTORY_SOURCE_DEFAULT_LABEL,
    optionLabel: INVENTORY_SOURCE_LABELS.all,
    sources: allSources,
  });

  if (savedRaw === null || savedRaw === 'all') return everything('default');
  if (!isInventorySourceValue(savedRaw) || !isInventorySourceConnected(savedRaw, connections)) return everything('fallback');

  const label = INVENTORY_SOURCE_LABELS[savedRaw];
  return { value: savedRaw, saved: savedRaw, origin: 'saved', label, optionLabel: label, sources: [label] };
}
